import { TrendingDown, SearchX, Search } from 'lucide-react';
import type { FunnelStep, SearchInsight } from '../types';

export function FunnelCard({ funnel }: { funnel: FunnelStep[] }) {
  const maxCount = Math.max(...funnel.map(s => s.count), 1);

  return (
    <div className="oem-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-oem-navy">Conversion Funnel</h3>
        <span className="text-xs text-oem-muted">Cached commerce events</span>
      </div>
      <div className="space-y-2.5">
        {funnel.map((step, i) => (
          <div key={step.key}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-oem-700 font-medium">{step.label}</span>
              <span className="flex items-center gap-2">
                {step.conversionFromPrevious !== null && (
                  <span className={`text-xs font-medium ${step.conversionFromPrevious >= 50 ? 'text-status-automation' : 'text-accent-600'}`}>
                    {step.conversionFromPrevious.toFixed(0)}%
                  </span>
                )}
                <span className="font-semibold text-oem-navy w-10 text-right">{step.count}</span>
              </span>
            </div>
            <div className="h-7 bg-oem-100 rounded-md overflow-hidden">
              <div
                className={`h-full rounded-md transition-all ${i === funnel.length - 1 ? 'bg-accent-500' : 'bg-oem-800'}`}
                style={{ width: `${Math.max((step.count / maxCount) * 100, step.count > 0 ? 3 : 0)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      {funnel.length > 1 && funnel[0].count > 0 && (
        <p className="mt-4 text-xs text-oem-muted flex items-center gap-1.5">
          <TrendingDown className="w-3.5 h-3.5" />
          Overall view-to-order conversion: {((funnel[funnel.length - 1].count / funnel[0].count) * 100).toFixed(1)}%
        </p>
      )}
    </div>
  );
}

export function SearchInsightsCard({ searches }: { searches: SearchInsight[] }) {
  const zeroResultSearches = searches.filter(s => s.zeroResults);
  const totalSearches = searches.reduce((sum, s) => sum + s.searches, 0);

  return (
    <div className="oem-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-oem-navy">Search Intelligence</h3>
        <span className="text-xs text-oem-muted">{totalSearches} searches tracked</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 bg-oem-100 rounded-lg">
          <p className="text-xs text-oem-muted">Unique Queries</p>
          <p className="text-lg font-bold text-oem-navy">{searches.length}</p>
        </div>
        <div className="p-3 bg-accent-50 border border-accent-200 rounded-lg">
          <p className="text-xs text-accent-700">Zero-Result Queries</p>
          <p className="text-lg font-bold text-accent-600">{zeroResultSearches.length}</p>
        </div>
      </div>

      {searches.length > 0 ? (
        <div className="max-h-52 overflow-y-auto divide-y divide-oem-100">
          {searches.map((s) => (
            <div key={`${s.query}-${s.type}`} className="py-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                {s.zeroResults ? (
                  <SearchX className="w-4 h-4 text-accent-500 shrink-0" />
                ) : (
                  <Search className="w-4 h-4 text-oem-400 shrink-0" />
                )}
                <span className="text-sm font-mono text-oem-800 truncate">{s.query}</span>
                <span className="text-xs text-oem-muted shrink-0">{s.type.replace('_', ' ')}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-oem-muted">{s.searches}×</span>
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  s.zeroResults
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-status-automation/10 text-status-automation'
                }`}>
                  {s.zeroResults ? '0 results' : `${s.resultsCount} results`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-oem-muted">No search events in the local cache yet.</p>
      )}

      {zeroResultSearches.length > 0 && (
        <p className="mt-3 text-xs text-accent-700">
          Zero-result queries indicate catalog gaps — candidates for new part listings.
        </p>
      )}
    </div>
  );
}
